import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAnalytics } from '../context/AnalyticsContext'
import { useAuth } from '../context/AuthContext'
import { loadLikesYou, loadMatches, loadYourLikes, getHasNewLikesAndMatches } from '../lib/likes'
import { saveLike, saveDislike, checkDailySuperLikeLimit, getSuperLikeResetTimeString, incrementDailySuperLikeCount } from '../lib/explore'
import { getExistingChat } from '../lib/chats'
import { useNewLikes } from '../context/NewLikesContext'
import ProfileModal from './ProfileModal'
import CompatibilityModal from './CompatibilityModal'
import Loader from './Loader'
import './Likes.css'

const Likes = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { user, profile } = useAuth()
  
  const SEGMENTS = [
    { key: 'likes-you', label: t('likes.likesYou'), emptyTitle: t('likes.likesYouEmpty'), emptyDesc: t('likes.likesYouEmptyDesc'), emptyEmoji: '💖' },
    { key: 'matches', label: t('likes.matches'), emptyTitle: t('likes.matchesEmpty'), emptyDesc: t('likes.matchesEmptyDesc'), emptyEmoji: '💞' },
    { key: 'your-likes', label: t('likes.yourLikes'), emptyTitle: t('likes.yourLikesEmpty'), emptyDesc: t('likes.yourLikesEmptyDesc'), emptyEmoji: '💘' }
  ]
  const [segment, setSegment] = useState('likes-you')
  const [likesYou, setLikesYou] = useState([])
  const [matches, setMatches] = useState([])
  const [yourLikes, setYourLikes] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalProfile, setModalProfile] = useState(null)
  const [compatibilityProfile, setCompatibilityProfile] = useState(null)
  const [segmentDotLikes, setSegmentDotLikes] = useState(false)
  const [segmentDotMatches, setSegmentDotMatches] = useState(false)
  const { markSeen } = useNewLikes()

  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
  const isFemale = profile?.gender === 'female'

  useEffect(() => {
    if (!user?.id) return
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    getHasNewLikesAndMatches(user.id).then((r) => {
      setSegmentDotLikes(r.hasNewLikes)
      setSegmentDotMatches(r.hasNewMatches)
      markSeen(user.id)
    })
  }, [user?.id, markSeen])

  const loadAll = async () => {
    if (!user) return
    setLoading(true)
    try {
      const [ly, ma, yl] = await Promise.all([
        loadLikesYou(user.id),
        loadMatches(user.id),
        loadYourLikes(user.id)
      ])
      setLikesYou(ly)
      setMatches(ma)
      setYourLikes(yl)
    } catch (e) {
      console.error('[Likes] loadAll', e)
    } finally {
      setLoading(false)
    }
  }

  const handleGetAstroPlus = () => {
    trackEvent('get_astro_plus_clicked', { location: 'likes' })
    navigate('/paywall?reason=subscription', { state: { from: location.pathname } })
  }

  const matchIds = new Set(matches.map((m) => m.id))
  const likesYouExcludingMatches = likesYou.filter((p) => !matchIds.has(p.id))

  const getList = () => {
    if (segment === 'likes-you') return likesYouExcludingMatches
    if (segment === 'matches') return matches
    return yourLikes
  }

  const handleItemClick = (item) => {
    if (segment === 'likes-you' && !hasSubscription && !isFemale) {
      trackEvent('paywall_shown', { reason: 'likes_you' })
      navigate('/paywall?reason=likes_you', { state: { from: location.pathname } })
      return
    }
    setModalProfile(item)
  }

  const getModalMode = () => {
    if (segment === 'likes-you') return 'likes-you'
    if (segment === 'matches') return 'matches'
    return 'your-likes'
  }

  const handleModalChat = async () => {
    if (!user || !modalProfile) return
    trackEvent('likes_chat_clicked', { segment })
    const { data: existingChat } = await getExistingChat(user.id, modalProfile.id)
    setModalProfile(null)
    if (existingChat) {
      navigate(`/chat/${existingChat.id}`)
    } else {
      navigate(`/chat/new/${modalProfile.id}`)
    }
  }

  const handleModalLike = async () => {
    if (!user || !modalProfile || segment !== 'likes-you') return
    trackEvent('card_liked')
    const { match, error } = await saveLike(user.id, modalProfile.id, false)
    if (error) return
    setLikesYou((prev) => prev.filter((p) => p.id !== modalProfile.id))
    setModalProfile(null)
    if (match) {
      const { data: existingChat } = await getExistingChat(user.id, modalProfile.id)
      if (existingChat) navigate(`/chat/${existingChat.id}`)
      else navigate(`/chat/new/${modalProfile.id}`)
    }
  }

  const handleModalSuperLike = async () => {
    if (!user || !modalProfile || segment !== 'likes-you') return
    if (!hasSubscription && !isFemale) {
      trackEvent('paywall_shown', { reason: 'super_like_no_access' })
      navigate('/paywall?reason=super_like_no_access', { state: { from: location.pathname } })
      return
    }
    const timezone = profile?.timezone || 'UTC'
    const limitData = await checkDailySuperLikeLimit(user.id, timezone)
    if (!limitData.canSuperLike) {
      trackEvent('super_like_limit_reached', { from: 'likes' })
      const resetIn = getSuperLikeResetTimeString(timezone)
      alert(t('likes.superLikeLimit', { resetIn }))
      return
    }
    trackEvent('star_button_clicked')
    const { match, error } = await saveLike(user.id, modalProfile.id, true)
    if (error) return
    await incrementDailySuperLikeCount(user.id, timezone)
    setLikesYou((prev) => prev.filter((p) => p.id !== modalProfile.id))
    setModalProfile(null)
    if (match) {
      const { data: existingChat } = await getExistingChat(user.id, modalProfile.id)
      if (existingChat) navigate(`/chat/${existingChat.id}`)
      else navigate(`/chat/new/${modalProfile.id}`)
    }
  }

  const handleModalDislike = async () => {
    if (!user || !modalProfile || segment !== 'likes-you') return
    trackEvent('card_disliked')
    await saveDislike(user.id, modalProfile.id)
    setLikesYou((prev) => prev.filter((p) => p.id !== modalProfile.id))
    setModalProfile(null)
  }

  const handleSeeCompatibility = () => {
    if (!hasSubscription) {
      trackEvent('paywall_shown', { reason: 'compatibility_details' })
      navigate('/paywall?reason=compatibility_details', { state: { from: location.pathname } })
      return
    }
    if (modalProfile) {
      setCompatibilityProfile(modalProfile)
      setModalProfile(null)
    }
  }

  const list = getList()
  const currentSegment = SEGMENTS.find((s) => s.key === segment)

  return (
    <div className="likes-screen">
      <div className="likes-top-bar">
        <button type="button" className="logo-large logo-btn" onClick={() => navigate('/app')} aria-label="Go to Explore">
          <img src="/Logo.png" alt="AstroMatch" />
        </button>
        {!hasSubscription && (
          <button type="button" className="astro-plus-btn" onClick={handleGetAstroPlus}>
            {t('likes.getAstroPlus')}
          </button>
        )}
      </div>

      <div className="likes-segments">
        {SEGMENTS.map((s) => {
          const showDot = (s.key === 'likes-you' && segmentDotLikes) || (s.key === 'matches' && segmentDotMatches)
          return (
            <button
              key={s.key}
              type="button"
              className={`likes-segment-btn ${segment === s.key ? 'active' : ''}`}
              onClick={() => {
                if (s.key === 'likes-you') setSegmentDotLikes(false)
                if (s.key === 'matches') setSegmentDotMatches(false)
                setSegment(s.key)
              }}
            >
              <span className="likes-segment-label-wrap">
                {s.label}
                {showDot && <span className="likes-segment-badge" aria-hidden />}
              </span>
            </button>
          )
        })}
      </div>

      <div className="likes-content">
        {loading ? (
          <div className="likes-loading"><Loader text={t('common.loading')} /></div>
        ) : list.length === 0 ? (
          <div className="likes-empty-state">
            <div style={{ fontSize: '48px', marginBottom: '24px' }}>{currentSegment?.emptyEmoji || '💖'}</div>
            <h2 className="likes-empty-title">{currentSegment?.emptyTitle}</h2>
            <p className="likes-empty-desc">{currentSegment?.emptyDesc}</p>
          </div>
        ) : (
          <div className="likes-grid">
            {list.map((item) => (
              <button
                key={item.id}
                type="button"
                className="likes-grid-item"
                onClick={() => handleItemClick(item)}
              >
                <div className={`likes-grid-photo ${segment === 'likes-you' && !hasSubscription && !isFemale ? 'blurred' : ''}`}>
                  {item.photos && item.photos[0] ? (
                    <img src={item.photos[0]} alt={item.name} />
                  ) : (
                    <div className="likes-grid-placeholder">{item.name?.[0] || '?'}</div>
                  )}
                </div>
                <span className="likes-grid-name">{item.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {modalProfile && (
        <ProfileModal
          profile={modalProfile}
          onClose={() => setModalProfile(null)}
          mode={getModalMode()}
          hasSubscription={hasSubscription}
          isFemale={isFemale}
          onLike={handleModalLike}
          onDislike={handleModalDislike}
          onSuperLike={handleModalSuperLike}
          onChat={handleModalChat}
          onSeeCompatibility={handleSeeCompatibility}
        />
      )}

      {compatibilityProfile && (
        <CompatibilityModal
          profile={compatibilityProfile}
          currentProfile={profile || { id: user?.id, date_of_birth: null }}
          fullContent={profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'}
          onClose={() => setCompatibilityProfile(null)}
        />
      )}
    </div>
  )
}

export default Likes
